import { env, functionsBase } from '../lib/env'

/**
 * The ONLY path that sends a sign-in email from this app. The email now
 * carries a 6-digit code and no link (see supabase/templates/); the request
 * itself is unchanged.
 *
 * Never call `supabase.auth.signInWithOtp()` from the page. `otp-request` is
 * what enforces the allowlist, the throttle and the send log, and it is
 * deliberately built so that not-allowlisted, inactive, throttled and
 * provider-error are indistinguishable to the caller in status, body and
 * approximate timing. Calling signInWithOtp directly would bypass all of that
 * and turn the sign-in screen into a rep-roster oracle.
 *
 * Because every outcome that depends on WHO asked returns the same uniform 200,
 * this function has nothing useful to report about a specific address — and so
 * it returns void. That is the point. There is intentionally no success/failure
 * result for the UI to branch on.
 *
 * The one thing the caller must also get right: show the same message with the
 * same timing even when THIS function throws. A network failure or a CORS
 * refusal is still an outcome the UI must not distinguish. See SignInScreen's
 * caller in AuthProvider.
 *
 * `next`: the validated return destination, or null. With code-only templates
 * the server-built `emailRedirectTo` no longer appears in any email, so this is
 * vestigial, but harmless and still validated. otp-request ports
 * `safeReturnPath`'s exact rules server-side and enforces them independently
 * — this is not the auth boundary, sending an already-invalid `next` here
 * would just mean the server's 400 fires instead of nothing, so this
 * function does not re-validate. The caller (AuthProvider) is what runs it
 * through `safeReturnPath` before it ever reaches here.
 */
export async function requestSignInCode(email: string, next: string | null): Promise<void> {
  // Not `supabase.functions.invoke`: that throws a typed error on non-2xx, and
  // shaping the UI around a thrown error invites branching on the outcome. A
  // bare fetch whose response we deliberately ignore matches the contract.
  //
  // This is a cross-origin request to the Supabase project, not a same-origin
  // path, so the Pages catch-all HTML-with-200 hazard does not apply and no
  // content-type guard is needed here. (Same-origin fetches still owe that
  // guard — task 2.1e.)
  await fetch(`${functionsBase}/otp-request`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // otp-request runs with verify_jwt = false, but the platform gateway
      // still wants a key on the request.
      apikey: env.supabasePublishableKey,
      Authorization: `Bearer ${env.supabasePublishableKey}`,
    },
    body: JSON.stringify(next ? { email, next } : { email }),
  })

  // Response deliberately not inspected. Nothing in it varies by address, and
  // reading it would only create somewhere for a future edit to leak a
  // distinction into the UI.
}
